import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle } from "lucide-react";

const services = [
  {
    id: "website-development",
    title: "Website Development",
    description:
      "CBS provides expert website development, creating, designing, and maintaining functional, high-performance digital spaces.",
    details: [
      "Static Websites",
      "Dynamic Websites",
      "CMS Solutions",
      "Responsive Web Apps",
    ],
    img: "/services/webdev.jpg",
  },
  {
    id: "application-development",
    title: "Application Development",
    description:
      "Custom software solutions built for web, mobile, desktop, and cross-platform environments. We create scalable, secure, and user-friendly applications tailored to business goals.",
    details: [
      "Web Application Development",
      "Mobile Apps (iOS & Android)",
      "Desktop Software Solutions",
      "Cross-platform App Development",
    ],
    img: "/services/appdev.jpg",
  },

  {
    id: "ecommerce-solutions",
    title: "E-commerce Solutions",
    description:
      "Scale your business with our robust B2B and B2C platforms designed for seamless user experiences and high conversion.",
    details: [
      "B2C Marketplaces",
      "B2B Trade Portals",
      "Secure Payment Integration",
      "Inventory Management",
    ],
    img: "/services/ecomservice.jpg",
  },
  {
    id: "ai-integration",
    title: "AI Integration Services",
    description:
      "Leverage pre-trained models and fine-tuning to embed intelligence into your business processes, from chatbots to predictive analytics.",
    details: [
      "Workflow Automation",
      "NLP & Chatbots",
      "Computer Vision",
      "Model Fine-Tuning",
    ],
    img: "/services/aiservices.jpg",
  },
  {
    id: "cloud-managed",
    title: "Cloud & Managed Services",
    description:
      "Complete management of cloud resources including migration, security, and optimization powered by AWS Certified Engineers.",
    details: [
      "Cloud Migration",
      "System Upgrades",
      "Data Center Virtualization",
      "24/7 Maintenance",
    ],
    img: "/services/cloud.jpg",
  },
];

export default function ServiceDetail() {
  const { id } = useParams();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Search in services only
  const item = services.find((item) => item.id === id);

  if (!item) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-4xl font-black mb-4">Service Not Found</h1>
          <Link to="/services" className="text-[#05DFAD] hover:underline">
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white font-sans pb-32">
      {/* Hero Section */}
      <section className="relative min-h-[300px] md:h-[400px] flex items-center justify-center overflow-hidden pt-20 md:pt-20">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19] via-transparent to-[#0B0F19]"></div>
        </div>

        <div className="relative z-10 text-center px-4 md:px-6 max-w-5xl">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6 md:mb-8">
            <ArrowLeft size={20} />
            Back to Services
          </Link>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black uppercase italic tracking-tighter leading-[0.9] md:leading-[0.85] mb-4 md:mb-6">
            {item.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="max-w-3xl mx-auto text-gray-400 text-sm md:text-lg lg:text-xl font-medium leading-relaxed px-2">
            {item.description}
          </motion.p>
        </div>
      </section>

      {/* Details Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
          {/* Image */}
          {item.img && (
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative order-2 md:order-1">
              <div className="absolute -inset-2 md:-inset-4 bg-gradient-to-r from-[#007BFF] to-[#05DFAD] rounded-2xl md:rounded-3xl blur-xl opacity-10"></div>
              <div className="relative bg-[#1A202C] rounded-2xl md:rounded-3xl border border-white/5 p-6 md:p-12 flex items-center justify-center">
                <img
                  src={item.img}
                  alt={item.title}
                  className="max-w-full rounded-2xl max-h-[250px] md:max-h-[400px] object-contain drop-shadow-2xl"
                />
              </div>
            </motion.div>
          )}

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6 md:space-y-8 order-1 md:order-2">
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-white uppercase italic tracking-tighter mb-4 md:mb-6">
                Core Features
              </h2>
              <div className="space-y-3 md:space-y-4">
                {item.details.map((detail, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-start gap-3 bg-[#161B22]/50 border border-white/10 p-4 rounded-xl hover:border-[#05DFAD]/30 transition-all">
                    <CheckCircle className="w-5 h-5 text-[#05DFAD] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm md:text-base leading-relaxed">
                      {detail}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="pt-4 flex gap-4">
              <Link
                to="/contact#contact-form"
                state={{
                  source: "service",
                  serviceTitle: item.title,
                }}
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#007BFF] to-[#05DFAD] rounded-xl text-white font-bold uppercase tracking-widest text-sm hover:opacity-90 transition-opacity shadow-lg">
                Request Demo
              </Link>
              {/*  <Link
                to="/#solutions"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#05DFAD] to-[#007BFF] rounded-xl text-white font-bold uppercase tracking-widest text-sm hover:opacity-90 transition-opacity shadow-lg">
                Learn More
              </Link> */}
            </motion.div>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
